import { FirebaseAuthentication, User } from '@capacitor-firebase/authentication';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useMutation } from '@tanstack/react-query';
import {
  type Dispatch,
  type PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { logCreateMutation } from '../../../../@api-client/@tanstack/react-query.gen';
import { useAppMessaging } from '../../app-messaging/use-app-messaging';
import { UserActivityTracker } from '../../user/user-activity-tracker.service';
import { isTokenExpired } from '../token-utils';
import { INACTIVITY_TIMEOUT_HOURS } from '../utils/constants';
import { getSessionCapabilities } from './auth-session-capabilities';
import { AuthContext } from './auth.context';

// ─── State Machine ────────────────────────────────────────────────────────────

export type AuthState =
  | { status: 'initialising' }
  | { status: 'authenticated'; token: string; user: User }
  | { status: 'unauthenticated' }
  | { status: 'signing-out' };

export type AuthAction =
  | { type: 'AUTH_RESOLVED'; token: string; user: User }
  | { type: 'AUTH_CLEARED' }
  | { type: 'SIGN_OUT_INITIATED' }
  | { type: 'SIGN_OUT_COMPLETE' }
  | { type: 'TOKEN_REFRESHED'; token: string };

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit tests only
export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_RESOLVED':
      if (state.status === 'signing-out') return state;
      return { status: 'authenticated', token: action.token, user: action.user };

    case 'AUTH_CLEARED':
      if (state.status === 'signing-out') return state;
      return { status: 'unauthenticated' };

    case 'SIGN_OUT_INITIATED':
      return { status: 'signing-out' };

    case 'SIGN_OUT_COMPLETE':
      return { status: 'unauthenticated' };

    case 'TOKEN_REFRESHED':
      if (state.status !== 'authenticated') return state;
      return { ...state, token: action.token };

    default:
      return state;
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

type AuthProviderProps = PropsWithChildren & {
  isContentWindow?: boolean;
  offlineSessionEnabled?: boolean;
};

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MAX_SESSION_MS = 30 * 24 * 60 * 60 * 1000;
const LOADING_TIMEOUT_MS = 10_000;
const INACTIVITY_CHECK_INTERVAL_MS = 60_000;
const INACTIVITY_DOM_EVENTS = ['click', 'scroll', 'touchstart'] as const;
const LOGIN_TIME_KEY = 'login_time';
const IDLE_TIME_KEY = 'application_idle';

const isRunningInStorybook = () =>
  typeof window !== 'undefined' &&
  (Boolean((import.meta as any).env?.STORYBOOK) ||
    Boolean((window as any).__STORYBOOK__) ||
    Boolean((window as any).__STORYBOOK_PREVIEW__));

const toTokenOrNull = (raw: string): string | null =>
  typeof raw === 'string' && raw.trim().length > 0 ? raw : null;

async function assertSessionWithinMaxAge() {
  const rawLoginTime = localStorage.getItem(LOGIN_TIME_KEY);
  if (rawLoginTime) {
    const sessionAge = Date.now() - parseInt(rawLoginTime, 10);
    if (sessionAge > MAX_SESSION_MS) {
      throw new Error('Session exceeded 30-day limit.');
    }
  } else {
    localStorage.setItem(LOGIN_TIME_KEY, Date.now().toString());
  }
}

// ─── useFirebaseAuthListener ──────────────────────────────────────────────────
// Subscribes to Firebase auth state changes and dispatches AUTH_RESOLVED or AUTH_CLEARED.
// Not active in child iframes or Storybook — those environments don't own Firebase auth.
//
// Handles a platform-specific race condition at startup:
//   Native (iOS): addListener() is async via the Capacitor bridge, so authStateChange
//     can fire before the listener is registered. The `listenerRegisteredWithEvent` flag
//     detects this. We fall back to getCurrentUser() and trust a null result as genuine.
//   Web: getCurrentUser() returns null while Firebase restores the session from localStorage.
//     A null result here is NOT genuine sign-out — it's a transient loading state.
//     We ignore null on web and let authStateChange deliver the real state shortly after.

function useFirebaseAuthListener(
  isContentWindow: boolean,
  isStorybook: boolean,
  authStateRef: RefObject<AuthState>,
  dispatch: Dispatch<AuthAction>,
  lastTokenFetchAtRef: RefObject<number>,
  logOut: () => Promise<void>,
  offlineSessionEnabled: boolean,
) {
  useEffect(() => {
    if (isContentWindow || isStorybook) return;

    const effect = { cancelled: false };
    let authListener: { remove: () => void } | null = null;
    let tokenListener: { remove: () => void } | null = null;

    const resolveUserSession = async (user: User | null) => {
      if (effect.cancelled) return;

      if (!user) {
        dispatch({ type: 'AUTH_CLEARED' });
        return;
      }

      if (authStateRef.current.status === 'signing-out') {
        return;
      }

      try {
        const { token } = await FirebaseAuthentication.getIdToken();

        if (isTokenExpired(token)) {
          if (offlineSessionEnabled && authStateRef.current.status === 'authenticated') {
            return;
          }
          await logOut();
          return;
        }

        dispatch({ type: 'AUTH_RESOLVED', token: toTokenOrNull(token) ?? '', user });
        lastTokenFetchAtRef.current = Date.now();
      } catch {
        if (!offlineSessionEnabled || authStateRef.current.status !== 'authenticated') {
          dispatch({ type: 'AUTH_CLEARED' });
        }
      }
    };

    void (async () => {
      if (effect.cancelled) return;

      let listenerRegisteredWithEvent = false;

      authListener = await FirebaseAuthentication.addListener('authStateChange', async (result) => {
        if (effect.cancelled) return;
        listenerRegisteredWithEvent = true;
        await resolveUserSession(result.user);
      });

      if (!listenerRegisteredWithEvent && !effect.cancelled) {
        try {
          const { user } = await FirebaseAuthentication.getCurrentUser();
          if (!effect.cancelled && !listenerRegisteredWithEvent) {
            if (user) {
              await resolveUserSession(user);
            } else if (Capacitor.isNativePlatform()) {
              await resolveUserSession(null);
            }
          }
        } catch {
          if (!effect.cancelled) dispatch({ type: 'AUTH_CLEARED' });
        }
      }

      // A custom-token sign-in for the same uid (a two-step pass) changes the token without an authStateChange.
      tokenListener = await FirebaseAuthentication.addListener('idTokenChange', ({ token }) => {
        if (effect.cancelled || !toTokenOrNull(token)) return;
        dispatch({ type: 'TOKEN_REFRESHED', token });
        lastTokenFetchAtRef.current = Date.now();
      });
      if (effect.cancelled) tokenListener?.remove();
    })();

    return () => {
      effect.cancelled = true;
      authListener?.remove();
      tokenListener?.remove();
    };
  }, [
    isContentWindow,
    isStorybook,
    logOut,
    dispatch,
    authStateRef,
    lastTokenFetchAtRef,
    offlineSessionEnabled,
  ]);
}

// ─── useTokenRefresh ──────────────────────────────────────────────────────────
// Keeps the token fresh on a timer and whenever the window regains focus.
// On native platforms, also listens to app resume/pause lifecycle events.
// Not active in child iframes — they receive tokens from the parent shell.

function useTokenRefresh(
  isContentWindow: boolean,
  isStorybook: boolean,
  mode: string,
  authStateRef: RefObject<AuthState>,
  dispatch: Dispatch<AuthAction>,
  lastTokenFetchAtRef: RefObject<number>,
  logOut: () => void,
  offlineSessionEnabled: boolean,
) {
  useEffect(() => {
    if (isContentWindow || isStorybook || mode === 'child') return;

    const refreshToken = async () => {
      try {
        const { user } = await FirebaseAuthentication.getCurrentUser();
        if (!user) return;

        if (!offlineSessionEnabled) {
          assertSessionWithinMaxAge().catch(() => logOut());
        }

        if (authStateRef.current.status !== 'authenticated') return;

        const { token } = await FirebaseAuthentication.getIdToken({ forceRefresh: true });

        if (authStateRef.current.status !== 'authenticated') return;

        dispatch({ type: 'TOKEN_REFRESHED', token: toTokenOrNull(token) ?? '' });
        lastTokenFetchAtRef.current = Date.now();
      } catch (error) {
        console.error('[AuthProvider] Failed to refresh token', error);
      }
    };

    const onFocus = () => void refreshToken();
    const onVisibilityVisible = () => {
      if (document.visibilityState === 'visible') void refreshToken();
    };

    // Cleanup references for Capacitor listeners
    let unsubscribeResume: (() => void) | null = null;

    // Register Capacitor app lifecycle listeners for native platforms
    const registerCapacitorListeners = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const resumeListener = await App.addListener('resume', async () => {
            await refreshToken();
          });
          unsubscribeResume = () => resumeListener.remove();
        } catch (error) {
          console.error(
            '[AuthProvider] Failed to register Capacitor app lifecycle listeners',
            error,
          );
        }
      }
    };

    void registerCapacitorListeners();

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityVisible);
    const intervalId = setInterval(() => void refreshToken(), TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityVisible);
      unsubscribeResume?.();
    };
  }, [
    isContentWindow,
    isStorybook,
    mode,
    authStateRef,
    dispatch,
    lastTokenFetchAtRef,
    logOut,
    offlineSessionEnabled,
  ]);
}

// ─── useLoadingTimeout ──────────────────────────────────────────────────────
// Safety net: if auth never resolves (Firebase SDK failure, postMessage never
// arrives), transition to unauthenticated after LOADING_TIMEOUT_MS so the user
// is not stuck on a spinner forever.

function useLoadingTimeout(authStateRef: RefObject<AuthState>, dispatch: Dispatch<AuthAction>) {
  useEffect(() => {
    const id = setTimeout(() => {
      if (authStateRef.current.status === 'initialising') {
        dispatch({ type: 'AUTH_CLEARED' });
      }
    }, LOADING_TIMEOUT_MS);

    return () => clearTimeout(id);
  }, [authStateRef, dispatch]);
}

// ─── useIframeMessaging ─────────────────────────────────────────────────────────
// Syncs auth state between parent shell and embedded child iframes via postMessage.
// Parent broadcasts token + user on every auth change, INCLUDING a bare token
// refresh: a child never mints its own token, so a broadcast keyed only on the
// user object leaves it holding the boot token until it expires an hour later.
// Child receives token + user and dispatches AUTH_RESOLVED.

function useIframeMessaging(
  isContentWindow: boolean,
  mode: string,
  firebaseUser: User | null,
  accessToken: string | null,
  logOut: (isLocalOnly?: boolean) => Promise<void>,
  dispatch: Dispatch<AuthAction>,
  broadcastToChildren: ReturnType<typeof useAppMessaging>['broadcastToChildren'],
  onMessage: ReturnType<typeof useAppMessaging>['onMessage'],
  send: ReturnType<typeof useAppMessaging>['send'],
) {
  const activityTrackerRef = useRef(new UserActivityTracker());

  useEffect(() => {
    if (isContentWindow || mode !== 'parent' || !firebaseUser || !accessToken) return;

    broadcastToChildren({
      type: 'auth-data',
      payload: {
        token: accessToken,
        user: JSON.parse(JSON.stringify(firebaseUser)),
      },
    });
  }, [broadcastToChildren, accessToken, mode, isContentWindow, firebaseUser]);

  useEffect(() => {
    const unsubscribe = onMessage((message) => {
      if (message.type === 'app-ready') return;

      if (message.type === 'user-activity') {
        activityTrackerRef.current.updateActivityToNow();
        return;
      }

      if (message.type === 'sign-out') {
        // Notify the embedded apps (a parent-mode send() only reaches the
        // shell's own window, which nothing listens to under the trust rules).
        void logOut().then(() => broadcastToChildren({ type: 'sign-out-complete' }));
        return;
      }

      if (message.type === 'request-sign-out') {
        void logOut();
        return;
      }

      if (message.type === 'auth-data' || message.type === 'init') {
        // init carries the same auth payload nested under `auth` (the boot
        // handshake); auth-data remains the ongoing refresh channel.
        const raw =
          message.type === 'init'
            ? (message as { payload?: { auth?: { user?: unknown; token?: unknown } } }).payload
                ?.auth
            : (message as { payload?: { user?: unknown; token?: unknown } }).payload;

        const user = raw?.user && typeof raw.user === 'object' ? (raw.user as User) : undefined;
        const token = typeof raw?.token === 'string' ? toTokenOrNull(raw.token) : null;

        if (token && user) {
          dispatch({ type: 'AUTH_RESOLVED', token, user });
        }
      }
    });

    return () => unsubscribe();
  }, [onMessage, send, broadcastToChildren, logOut, dispatch]);
}

// ─── useInactivityLogout ─────────────────────────────────────────────────────
// Monitors DOM activity in the parent shell and logs out after
// INACTIVITY_TIMEOUT_HOURS of idle time. Only active when the user is
// authenticated in a standalone or parent-shell context.
//
// Safety guarantees:
//   - logOutRef: stores the latest logOut without making it a dep, so the
//     timer is never reset when logOut's reference changes between renders.
//   - hasInitiatedLogout flag: prevents the interval from firing a second
//     logout call before React has processed the state change to 'signing-out'.

function useInactivityLogout(
  isContentWindow: boolean,
  isStorybook: boolean,
  mode: string,
  status: AuthState['status'],
  logOut: (isLocalOnly?: boolean, inactivityTrigger?: boolean) => Promise<void>,
  offlineSessionEnabled: boolean,
) {
  const logOutRef = useRef(logOut);
  logOutRef.current = logOut;

  useEffect(() => {
    const shouldMonitor =
      !offlineSessionEnabled &&
      !isContentWindow &&
      !isStorybook &&
      mode === 'parent' &&
      status === 'authenticated';

    if (!shouldMonitor) return;

    const tracker = new UserActivityTracker();
    tracker.updateActivityToNow();

    let hasInitiatedLogout = false;

    const onActivity = () => tracker.updateActivityToNow();
    INACTIVITY_DOM_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true }),
    );

    const intervalId = setInterval(() => {
      if (hasInitiatedLogout) return;
      if (!tracker.lastActivityOlderThanHours(INACTIVITY_TIMEOUT_HOURS)) return;

      hasInitiatedLogout = true;
      void logOutRef.current(false, true).then(() => {
        window.location.replace('/login?reason=inactivity');
      });
    }, INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      INACTIVITY_DOM_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
    };
  }, [isContentWindow, isStorybook, mode, status, offlineSessionEnabled]);
}

// ─── AuthProvider ─────────────────────────────────────────────────────────────

export const AuthProvider = ({
  children,
  isContentWindow = false,
  offlineSessionEnabled = false,
}: AuthProviderProps) => {
  const isStorybook = isRunningInStorybook();
  const { mode, onMessage, broadcastToChildren, send } = useAppMessaging();

  const initialState: AuthState = isStorybook
    ? { status: 'unauthenticated' }
    : { status: 'initialising' };

  const [state, dispatch] = useReducer(authReducer, initialState);

  const authStateRef = useRef<AuthState>(state);
  authStateRef.current = state;

  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current = state.status === 'authenticated' ? state.token : null;

  const firebaseUserRef = useRef<User | null>(null);
  firebaseUserRef.current = state.status === 'authenticated' ? state.user : null;

  const lastTokenFetchAtRef = useRef<number>(0);

  const getAccessToken = useCallback(() => accessTokenRef.current, []);

  const { mutate: logCreateMutate } = useMutation({ ...logCreateMutation() });

  const logOut = useCallback(
    async (isLocalOnly = false, inactivityTrigger = false) => {
      const currentUser = firebaseUserRef.current;

      dispatch({ type: 'SIGN_OUT_INITIATED' });

      const meta = {
        message: `[${currentUser?.email}] Logged Out`,
        action: 'Log Out',
        logoutType: inactivityTrigger ? 'INACTIVITY_INITIATED' : 'USER_INITIATED',
        email: currentUser?.email || 'UNKNOWN',
        name: currentUser?.displayName || 'UNKNOWN',
      };

      if (!isLocalOnly) broadcastToChildren({ type: 'sign-out' });

      if (isStorybook) {
        dispatch({ type: 'SIGN_OUT_COMPLETE' });
        return;
      }

      try {
        if (isContentWindow) {
          if (!isLocalOnly) send({ type: 'request-sign-out' });
          dispatch({ type: 'SIGN_OUT_COMPLETE' });
          return;
        }

        localStorage.removeItem(LOGIN_TIME_KEY);
        localStorage.removeItem(IDLE_TIME_KEY);
        await FirebaseAuthentication.signOut();
      } finally {
        dispatch({ type: 'SIGN_OUT_COMPLETE' });
        logCreateMutate({
          body: [
            { type: 'audit', level: 'info', args: [], timestamp: new Date().toISOString(), meta },
          ],
        });
      }
    },
    [broadcastToChildren, isContentWindow, isStorybook, logCreateMutate, send],
  );

  useLoadingTimeout(authStateRef, dispatch);

  useFirebaseAuthListener(
    isContentWindow,
    isStorybook,
    authStateRef,
    dispatch,
    lastTokenFetchAtRef,
    logOut,
    offlineSessionEnabled,
  );
  useTokenRefresh(
    isContentWindow,
    isStorybook,
    mode,
    authStateRef,
    dispatch,
    lastTokenFetchAtRef,
    logOut,
    offlineSessionEnabled,
  );
  useIframeMessaging(
    isContentWindow,
    mode,
    state.status === 'authenticated' ? state.user : null,
    state.status === 'authenticated' ? state.token : null,
    logOut,
    dispatch,
    broadcastToChildren,
    onMessage,
    send,
  );
  useInactivityLogout(
    isContentWindow,
    isStorybook,
    mode,
    state.status,
    logOut,
    offlineSessionEnabled,
  );

  const sessionCapabilities = getSessionCapabilities(state, offlineSessionEnabled);

  const value = useMemo(
    () => ({
      isLoading: state.status === 'initialising',
      firebaseUser: state.status === 'authenticated' ? state.user : null,
      accessToken: state.status === 'authenticated' ? state.token : null,
      ...sessionCapabilities,
      getAccessToken,
      logOut,
    }),
    [state, sessionCapabilities, getAccessToken, logOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
